import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {getReport, ReportFilterValues, ReportPayload} from '@app/services/reports';
import {getAllActiveFranchise} from '@app/services/franchise';
import KpiStrip from './components/KpiStrip';
import ReportFilters, {FranchiseOption} from './components/ReportFilters';
import {
  ChartCard,
  ReportBarChart,
  ReportDonutChart,
  ReportHorizontalBarChart
} from './components/ReportChart';
import useReportExport from './hooks/useReportExport';
import {reportTableStyles} from './components/tableStyles';
import {humanise} from './components/labels';
import {findReportBySlug} from './reportCatalogue';

/**
 * REV3 — Materials Revenue.
 *
 * The first report built, so it also establishes the pattern the other twelve
 * follow: filter bar, KPI strip, charts, drill-down table, Excel export, all
 * driven by one API response.
 *
 * Two report-specific notes:
 *  - No centre filter. `orders` carries no centre_id, so offering one would be
 *    a control that silently does nothing.
 *  - No region filter. Geography hangs off postcodes, and 16 ordering
 *    franchises have no region at all while one spans two — so a region total
 *    would neither include everyone nor sum correctly. The franchise list
 *    answers the same question cleanly.
 */

const SLUG = 'materials-revenue';

const CURRENT_YEAR = new Date().getFullYear();

// Newest first. Orders on stage start in 2023; five years covers the data and
// leaves room ahead without offering years that can only ever be empty.
const YEARS = Array.from({length: 5}, (_, i) => CURRENT_YEAR - i);

const money = (v: any) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2
  }).format(Number(v) || 0);

const MaterialsRevenue = () => {
  const definition = findReportBySlug(SLUG);

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<FranchiseOption[]>([]);

  // Defaults to the current full year — whole calendar months, no partials.
  const [filters, setFilters] = useState<ReportFilterValues>({
    mode: 'year',
    year: CURRENT_YEAR,
    period: 'full',
    page: 1,
    limit: 25
  });
  const [applied, setApplied] = useState<ReportFilterValues>(filters);

  const {exportToExcel, exporting} = useReportExport('Materials Revenue');

  const load = useCallback(async (active: ReportFilterValues) => {
    setLoading(true);
    setError(null);
    const response = await getReport(SLUG, active);
    if (response && response.success) {
      setPayload(response);
    } else {
      setPayload(null);
      setError(response?.message || 'The report could not be loaded.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  // Franchise options. Follows the existing convention of checking
  // success === true before reading collections, same as ExceededThreshold.tsx.
  useEffect(() => {
    (async () => {
      const response = await getAllActiveFranchise();
      if (response?.success === true && Array.isArray(response.collections)) {
        setFranchises(
          response.collections.map((f: any) => ({value: f.id, label: f.name}))
        );
      }
    })();
  }, []);


  const collections = payload?.collections;
  const byPeriod = collections?.series?.by_period || [];
  const byItemType = collections?.series?.by_item_type || [];
  const topFranchises = collections?.series?.top_franchises || [];
  const detail = collections?.detail;

  const columns = [
    {
      name: 'Order',
      selector: (row: any) => row.order_name || `#${row.id}`,
      sortable: false,
      wrap: true
    },
    {name: 'Franchise', selector: (row: any) => row.franchise || '---', wrap: true},
    {name: 'Placed', selector: (row: any) => row.placed_on || '---', width: '110px'},
    {name: 'Dispatched', selector: (row: any) => row.dispatched_on || '---', width: '115px'},
    {
      name: 'Status',
      width: '120px',
      cell: (row: any) => (
        <span
          className={`badge badge-${
            row.order_status === 'dispatched' ? 'success'
              : row.order_status === 'cancelled' ? 'danger' : 'warning'
          }`}>
          {humanise(row.order_status)}
        </span>
      )
    },
    {
      name: 'Net',
      width: '110px',
      right: true,
      selector: (row: any) => money(row.net_price)
    },
    {
      name: 'Total',
      width: '120px',
      right: true,
      selector: (row: any) => money(row.total_price)
    },
    // Sent only under an item type or subject filter: the value of the matching
    // lines, which is what the headline sums. Total is still the whole order.
    ...((detail?.rows || []).some((row: any) => row.matched_value !== undefined)
      ? [{
          name: 'Matched',
          width: '120px',
          right: true,
          cell: (row: any) => (
            <span className="font-weight-bold">{money(row.matched_value)}</span>
          )
        }]
      : [])
  ];

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-7">
              <h1>{definition?.name || 'Materials Revenue'}</h1>
              <p className="text-muted mb-0">{definition?.question}</p>
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/reports">Reports</Link></li>
                <li className="breadcrumb-item active">Materials Revenue</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <ReportFilters
            values={filters}
            onChange={setFilters}
            onApply={() => setApplied({...filters, limit: applied.limit, page: 1})}
            onReset={(defaults) => {
              setFilters(defaults);
              setApplied(defaults);
            }}
            onExport={() => exportToExcel(payload, SLUG, applied)}
            exporting={exporting}
            franchises={franchises}
            years={YEARS}
            intervalLabel={collections?.filters_applied?.interval_label}
            show={{franchise: true, itemType: true, subject: true}}
            disabled={loading}
          />

          {error && (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          <KpiStrip kpis={collections?.kpis || []} loading={loading} />

          <div className="row">
            <div className="col-lg-7">
              <ChartCard
                title="Order value over time"
                subtitle={
                  collections?.filters_applied?.interval_label
                    ? `${collections.filters_applied.interval_label} totals`
                    : ' '
                }
                loading={loading}
                isEmpty={!byPeriod.length}>
                <ReportBarChart data={byPeriod} xKey="bucket" yKey="value" />
              </ChartCard>
            </div>
            <div className="col-lg-5">
              <ChartCard
                title="Value by item type"
                subtitle="Share of spend across the seven item types"
                loading={loading}
                isEmpty={!byItemType.length}>
                <ReportDonutChart data={byItemType} nameKey="item_type" valueKey="value" />
              </ChartCard>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <ChartCard
                title="Top franchises by spend"
                subtitle="The ten franchises spending most with head office"
                loading={loading}
                isEmpty={!topFranchises.length}
                height={340}>
                <ReportHorizontalBarChart
                  data={topFranchises}
                  labelKey="franchise"
                  valueKey="value"
                  height={320}
                  colour="#3D6DB5"
                />
              </ChartCard>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title text-capitalize">Orders</h3>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                customStyles={reportTableStyles}
                data={detail?.rows || []}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={detail?.total || 0}
                paginationDefaultPage={applied.page || 1}
                paginationPerPage={applied.limit || 25}
                paginationRowsPerPageOptions={[25, 50, 100]}
                onChangePage={(page) => setApplied({...applied, page})}
                onChangeRowsPerPage={(limit, page) =>
                  setApplied({...applied, limit, page})
                }
                noDataComponent={
                  <div className="text-muted py-4">
                    No orders match these filters.
                  </div>
                }
                highlightOnHover
                dense
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MaterialsRevenue;
